// Adquirentes do admin (croqui `screens/admin/adquirentes.tsx`, task WEB-80). Foco da
// tela: **a lista de adquirentes**. Quem tem `useQuery`/`useMutation` é esta rota; os
// componentes de `features/admin-acquirers` recebem dado por prop.
//
// As quatro escritas passam por `ADMIN_STEP_UP_ROUTES`: o `403 mfa_required` abre o
// `StepUpDialog` dentro de `api/client.ts` e a chamada original é reexecutada sozinha -
// nada disso é reimplementado aqui (`spec/17` §4.3).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { errorMessageOf } from '../api/errors.ts';
import {
  type Acquirer,
  type AcquirerMode,
  ADMIN_ACQUIRERS_QUERY_KEY,
  acquirersQueryOptions,
  createAcquirer,
  patchAcquirer,
  putDefaultAcquirer,
  testConnection,
} from '../api/queries/admin-acquirers.ts';
import { Btn } from '../components/kit/btn.tsx';
import { Footnote } from '../components/kit/footnote.tsx';
import { Mono } from '../components/kit/mono.tsx';
import { Notice } from '../components/kit/notice.tsx';
import { PageHeader } from '../components/kit/page-header.tsx';
import { Panel } from '../components/kit/panel.tsx';
import { Pill } from '../components/kit/pill.tsx';
import { AcquirerFormDrawer } from '../features/admin-acquirers/acquirer-form-drawer.tsx';
import { AcquirerList, DefaultByMode } from '../features/admin-acquirers/acquirer-list.tsx';
import {
  type AcquirerFormValues,
  createBodyFrom,
  isStepUpCancelled,
  patchBodyFrom,
} from '../features/admin-acquirers/model.ts';
import { defaultAcquirerQueryOptions } from '../features/admin-acquirers/queries.ts';

function AdminAcquirers() {
  const queryClient = useQueryClient();
  const acquirers = useQuery(acquirersQueryOptions());
  const defaultsQuery = useQuery(defaultAcquirerQueryOptions());

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Acquirer | undefined>(undefined);

  const rows = acquirers.data ?? [];
  const defaults = defaultsQuery.data ?? {};

  /** Uma invalidação de prefixo alcança a lista E o padrão por modo (`spec/17` §4.5). */
  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: [ADMIN_ACQUIRERS_QUERY_KEY] });
  };

  const save = useMutation({
    mutationFn: async (values: AcquirerFormValues) =>
      editing === undefined
        ? createAcquirer(createBodyFrom(values))
        : patchAcquirer(editing.id, patchBodyFrom(values, editing)),
    onSuccess: async () => {
      setFormOpen(false);
      setEditing(undefined);
      await refresh();
    },
  });

  /**
   * `test-connection` persiste `last_test_at`/`ok`/`error` (API-25): o resultado não fica
   * em estado local, a lista refaz a leitura e a linha mostra o que o SERVIDOR guardou.
   * Assim a coluna continua certa em outra aba e no dia seguinte.
   */
  const test = useMutation({
    mutationFn: async (acquirer: Acquirer) => testConnection(acquirer.id),
    onSettled: async () => {
      await refresh();
    },
  });

  const setDefault = useMutation({
    mutationFn: async (input: { mode: AcquirerMode; acquirerId: string }) =>
      putDefaultAcquirer({ mode: input.mode, acquirer_id: input.acquirerId }),
    onSuccess: async () => {
      await refresh();
    },
  });

  const openCreate = (): void => {
    save.reset();
    setEditing(undefined);
    setFormOpen(true);
  };

  // Step-up cancelado fecha o `StepUpDialog` e não é falha de tela (`spec/17` §4.3).
  const writeError = [test.error, setDefault.error].find(
    (error) => error != null && !isStepUpCancelled(error),
  );

  return (
    <>
      <PageHeader
        title="Adquirentes"
        sub="Parceiros PIX habilitados e o padrão por modo"
        path="/admin/v1/acquirers"
        size="lg"
        actions={
          <Btn kind="primary" icon="lock" onClick={openCreate}>
            Novo adquirente
          </Btn>
        }
      />

      <Notice tone="neutral" icon="lock" title="Credenciais nunca são exibidas.">
        A API devolve só quais campos estão configurados (
        <Mono size={11}>credentials.configured</Mono>
        ). Rotação e leitura exigem um novo valor, nunca o antigo.
      </Notice>

      {writeError !== undefined && (
        <div role="alert">
          <Notice tone="bad" title="Ação recusada">
            {errorMessageOf(writeError)}
          </Notice>
        </div>
      )}

      <Panel
        title="Adquirentes"
        sub="Ordenado por padrão live primeiro"
        {...(acquirers.isPending
          ? {}
          : { right: <Pill tone="info">{rows.length} cadastrados</Pill> })}
      >
        <AcquirerList
          acquirers={rows}
          defaults={defaults}
          isLoading={acquirers.isPending}
          // Erro só quando não há lista: com o polling de 30 s, um flake no refetch de
          // fundo trocaria a lista carregada por uma tela de falha.
          error={acquirers.data === undefined ? acquirers.error : undefined}
          onRetry={() => {
            void acquirers.refetch();
          }}
          onCreate={openCreate}
          onEdit={(acquirer) => {
            save.reset();
            setEditing(acquirer);
            setFormOpen(true);
          }}
          onTest={(acquirer) => {
            test.reset();
            test.mutate(acquirer);
          }}
          onSetDefault={(acquirer, mode) => {
            setDefault.reset();
            setDefault.mutate({ mode, acquirerId: acquirer.id });
          }}
          {...(test.isPending && test.variables !== undefined
            ? { testingId: test.variables.id }
            : {})}
          {...(setDefault.isPending && setDefault.variables !== undefined
            ? { settingDefaultId: setDefault.variables.acquirerId }
            : {})}
        />
        <Footnote left="teste de conexão roda credentials, balance e lookup contra o sandbox do parceiro" />
      </Panel>

      <Panel
        title="Padrão por modo"
        sub="Usado quando o seller não tem override em seller_acquirer_configs"
      >
        <DefaultByMode acquirers={rows} defaults={defaults} />
      </Panel>

      <AcquirerFormDrawer
        open={formOpen}
        {...(editing === undefined ? {} : { acquirer: editing })}
        isPending={save.isPending}
        error={save.error}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) {
            setEditing(undefined);
          }
        }}
        onSubmit={(values) => {
          save.mutate(values);
        }}
      />
    </>
  );
}

export const Route = createFileRoute('/admin/acquirers/')({ component: AdminAcquirers });
